import { defineStore } from 'pinia';
import { projectPhases, projectCampaignOutcomes, projectTypes, projectVersions } from '@/Models';
import InitialState from '@/InitialState';
import { addRecentlyViewedProjectId } from '@/Utils/RecentlyViewedProjectsUtility';
import ProjectTestingService from '@/Services/ProjectTestingService';
import { apiClient } from '@/Clients/ApiClient.js';

export const useProjectContextStore = defineStore('projectContext', {
    state: () => {
        addRecentlyViewedProjectId(InitialState.projectContext.projectID); // Agreed that it isn't the best place, but the update isn't triggered on initial load

        return {
            projectContext: {
                canEnableTestMode: false,
                creator: null,
                creatorID: null,
                hasProject: false,
                installmentsSettings: null,
                isDccEnabled: false,
                isStablePledgeEnabled: false,
                isInTestMode: false,
                project: null,
                projectID: null,
                projectPhaseChangedAt: null,
                settings: null,
                shippingCollectionMode: 0,
                snapshotPhase: null,
                testingContext: null,
                testingPhase: 0,
                ...InitialState.projectContext,
            },
        };
    },
    actions: {
        updateProjectContext(state, projectContext) {
            state.projectContext = projectContext;
            addRecentlyViewedProjectId(projectContext.projectID);
        },
        async enableTestingMode() {
            const projectTestingService = new ProjectTestingService(apiClient);
            await projectTestingService.toggleTestMode({ projectID: this.projectContext.projectID });
            window.location.reload(false);
        },
        async disableTestingMode() {
            const projectTestingService = new ProjectTestingService(apiClient);
            await projectTestingService.disableTestMode(this.projectContext.projectID);
            window.location.reload(false);
        },
        async toggleTestingMode() {
            if (this.isInTestMode) {
                return await this.disableTestingMode();
            }
            return await this.enableTestingMode();
        },
    },
    getters: {
        isProjectInDraftPhase: state => {
            return state.projectContext.project?.phase === projectPhases.crowdfundingDraft;
        },
        isProjectInLatePledgePhase: state => {
            return state.projectContext.project?.phase === projectPhases.latePledge;
        },
        isProjectInLatePledgeEndedPhase: state => {
            return state.projectContext.project?.phase === projectPhases.latePledgeEnded;
        },
        isProjectInCrowdfundingPhase: state => {
            return state.projectContext.project?.phase === projectPhases.crowdfunding;
        },
        isProjectInCrowdfundingEndedPhase: state => {
            return state.projectContext.project?.phase === projectPhases.crowdfundingEnded;
        },
        isProjectInPledgeManagerPhase: state => {
            return state.projectContext.project?.phase === projectPhases.pledgeManager;
        },
        isProjectInPledgeManagerCompletePhase: state => {
            return state.projectContext.project?.phase === projectPhases.pledgeManagerComplete;
        },
        isProjectInFundingPhase: state => { // @todo: find better name, late pledge ain't really funding phase
            return [projectPhases.crowdfunding, projectPhases.latePledge]
                .includes(state.projectContext.project?.phase);
        },
        isProjectInPhasePreview: state => {
            return state.projectContext && state.projectContext.snapshotPhase !== null;
        },
        isProjectCampaignFunded: state => {
            return state.projectContext.project?.campaignOutcome === projectCampaignOutcomes.success;
        },
        isProjectRetailType: state => {
            return [projectTypes.store, projectTypes.preorder].includes(state.projectContext.project?.originalType);
        },
        isStore: state => {
            return state.projectContext.project?.originalType === projectTypes.store;
        },
        // TODO: it is used currently to determine if installment plan can be paid off
        // it should be refactored to get validation from backend: CheckoutManager.ValidateInstallmentsCanBePaidOff
        // add this flag to projectContext.installmentsSettings
        isExternalCampaign: state => {
            return state.projectContext.project?.originalType !== projectTypes.crowdfunding;
        },
        isV2: state => {
            return state.projectContext.project?.version === projectVersions.v2;
        },
        isOriginalTypeExpressCrowdfunding: state => {
            return state.projectContext.project?.originalType === projectTypes.expressCrowdfunding;
        },
        isExpressCrowdfunding() {
            return this.isOriginalTypeExpressCrowdfunding && (this.isProjectInCrowdfundingPhase || this.isProjectInCrowdfundingEndedPhase);
        },
        effectivePhase: state => {
            return state.projectContext.snapshotPhase ?? state.projectContext.project?.phase;
        },
        projectID: state => state.projectContext.projectID,
        hasEarlyShippingCollectionEnabled: state => {
            return state.projectContext?.settings.earlyShippingCollectionEnabled;
        },
        hasOrderEditingEnabled: state => {
            return state.projectContext?.settings.allowEditOrders;
        },
        isDccEnabled: state => state.projectContext?.isDccEnabled,
        isStablePledgeEnabled: state => state.projectContext?.isStablePledgeEnabled,
        installmentsSettings: state => state.projectContext?.installmentsSettings,
        isOpenForOrders: state => state.projectContext?.project.isOpenForOrders,
        canEnableTestMode: state => state.projectContext.canEnableTestMode,
        isInTestMode: state => state.projectContext.isInTestMode,
        paymentProvider: state => state.projectContext?.paymentProvider,
    },
});